/**
 * The color scales written alongside the hex GeoJSON.
 *
 * Parsing only: no MapLibre import, so the legends and their tests can use
 * this without pulling the map library into jsdom.
 */

export type MeanPriceStop = {
  price: number;
  color: string;
};

export type PriceChangeStop = {
  pct: number;
  color: string;
};

const ascending = <T>(
  scale: unknown,
  valueOf: (stop: T) => unknown,
  build: (stop: T) => T,
): T[] => {
  if (!Array.isArray(scale)) {
    return [];
  }
  const stops: T[] = [];
  let previous = Number.NEGATIVE_INFINITY;
  for (const stop of scale as T[]) {
    const value = valueOf(stop);
    if (typeof value !== 'number' || typeof (stop as { color?: unknown })?.color !== 'string') {
      continue;
    }
    if (value <= previous) {
      continue;
    }
    previous = value;
    stops.push(build(stop));
  }
  return stops.length >= 2 ? stops : [];
};

/**
 * Stops MapLibre can paint, in ascending price order.
 *
 * Tied percentiles are dropped so a flat price distribution does not produce
 * an invalid interpolate expression. Fewer than two distinct prices means
 * there is no fill and no legend.
 */
export const readMeanPriceScale = (scale: unknown): MeanPriceStop[] =>
  ascending<MeanPriceStop>(
    scale,
    (stop) => stop?.price,
    (stop) => ({ price: stop.price, color: stop.color }),
  );

/**
 * Stops for the price-change layer, in ascending order.
 *
 * Same rules as the mean-price scale: ties are dropped, and fewer than two
 * distinct rates means there is no fill and no legend.
 */
export const readPriceChangeScale = (scale: unknown): PriceChangeStop[] =>
  ascending<PriceChangeStop>(
    scale,
    (stop) => stop?.pct,
    (stop) => ({ pct: stop.pct, color: stop.color }),
  );
